/**
 * LA MESA DE CONTROL — la parte PURA.
 *
 * Acá vive lo que la pantalla del anotador decide sin React, sin DOM y sin
 * red: el orden reglamentario de las firmas (instrucción 4.10), qué desenlace
 * de un challenge retiene el cupo, cuántos challenges quedan DESPUÉS de una
 * resolución, y cómo se compone el lote atómico `challenge_resolved` +
 * reconciliación que cierra la interrupción.
 *
 * ## POR QUÉ LA MESA EMITE EVENTOS Y EL BANCO NO
 *
 * El banco pide por intents y la mesa acepta (§5.2): eso ya tiene pantalla
 * (`pages/bench`). Lo que NO nacía de ninguna pantalla es lo que el reglamento
 * pone en manos de la mesa misma: acreditar al cuerpo arbitral
 * (`officials_set`), resolver el challenge (`challenge_resolved` — VCS art.
 * 25: la decisión es del 1er árbitro con el informe del Challenge Referee, y
 * la registra la mesa) y reconciliar el marcador. Esos entran por `POST
 * /events`, el mismo sync atómico e idempotente de la captura, con el `actor`
 * de la sesión del anotador — que el borde sella `verified` (ADR-016).
 *
 * ## PARIDAD CON `@scout/core`
 *
 * El portal consume JSON y **no ejecuta `@scout/core`** (misma regla que
 * `lib/log.ts` y `lib/competition-form.ts`): las listas cerradas y las dos
 * reglas de conteo están COPIADAS acá con su fuente anotada, y
 * `test/mesa.test.ts` las corre contra el motor de verdad. Si alguien toca el
 * reglamento allá y no acá, ese test se pone rojo con el campo exacto.
 *
 * ## LA LIMITACIÓN DICHA SIN ADORNOS
 *
 * `remainingAfterResolution` cuenta como FIVB (`countUnsuccessfulOnly: true`,
 * el default que corren los dos rulesets del repo): sólo el fallido descuenta.
 * Una competencia que apague ese flag contaría USOS, y esta cuenta —que no ve
 * la `SportConfig`— quedaría corta. El test de paridad lo fija; el día que
 * exista un ruleset así, el dato tiene que venir del API (el motor igual
 * valida el contador con `CHALLENGE_REMAINING_MISMATCH`, así que un evento mal
 * contado no pasa desapercibido en la validación de dominio).
 */
import type {
  Actor,
  ChallengeFault,
  ChallengeOutcome,
  ChallengeState,
  MatchRole,
  OfficialRef,
  ScoreAction,
  ScoutEvent,
  SheetSignatureRef,
  SheetState,
  TeamSide,
} from '@scout/core';
import type { MatchCredential } from '../../lib/api-client';
import { normalizeLog } from '../../lib/log';

// ---------------------------------------------------------------------------
// Listas cerradas del contrato (copiadas; la paridad la cuida test/mesa.test.ts)
// ---------------------------------------------------------------------------

/** Versión del envelope (`SCHEMA_VERSION` de `@scout/core/contracts/events`). */
export const MESA_SCHEMA_VERSION = 2 as const;

/**
 * Roles que forman la mesa y el cuerpo arbitral (regla 22.1 + 27/28). Copia de
 * `OFFICIAL_ROLES` en `packages/core/src/engine/validate.ts`: es lo que el
 * motor acepta en `officials_set` sin el warning `OFFICIAL_ROLE`.
 */
export const MESA_OFFICIAL_ROLES = [
  'referee1',
  'referee2',
  'challenge_referee',
  'reserve_referee',
  'scorer',
  'assistant_scorer',
  'line_judge',
  'supervisor',
  'replay_operator',
] as const satisfies readonly MatchRole[];

/**
 * Roles para los que la mesa emite credenciales, con si la credencial lleva
 * lado. Los de banco (delegado, capitán) SIEMPRE llevan equipo: sin lado, el
 * server rechaza sus intents (`no_team`) y el banco de A podría pedir por B.
 */
export const CREDENTIAL_ROLES: ReadonlyArray<{ role: MatchRole; needsTeam: boolean }> = [
  { role: 'referee1', needsTeam: false },
  { role: 'referee2', needsTeam: false },
  { role: 'challenge_referee', needsTeam: false },
  { role: 'reserve_referee', needsTeam: false },
  { role: 'scorer', needsTeam: false },
  { role: 'assistant_scorer', needsTeam: false },
  { role: 'line_judge', needsTeam: false },
  { role: 'supervisor', needsTeam: false },
  { role: 'replay_operator', needsTeam: false },
  { role: 'team_delegate', needsTeam: true },
  { role: 'team_captain', needsTeam: true },
  { role: 'scoreboard', needsTeam: false },
];

/**
 * Orden de firmas del cierre (instrucción 4.10 + regla 27.2.3.3). Copia del
 * default de `ClosureRulesSchema.signingOrder` (`@scout/core/contracts/sport`):
 * el anotador primero —su firma congela el digest—, después el asistente, los
 * capitanes, el 2do árbitro, el 1ro y el challenge referee.
 */
export const MESA_SIGNING_ORDER = [
  'scorer',
  'assistant_scorer',
  'team_captain',
  'referee2',
  'referee1',
  'challenge_referee',
] as const satisfies readonly MatchRole[];

/**
 * Firmas PREMATCH que congelan el roster (regla 4.1.3). Copia del default de
 * `ClosureRulesSchema.requirePrematchSignatures`.
 */
export const MESA_PREMATCH_ROLES = ['team_captain', 'team_delegate'] as const satisfies readonly MatchRole[];

/** Menú completo de faltas (copia de `CHALLENGE_FAULTS` de `@scout/core`). */
export const MESA_CHALLENGE_FAULTS = [
  'BALL_IN_OUT',
  'BLOCK_TOUCH',
  'NET_FAULT',
  'ANTENNA_TOUCH',
  'FOOT_FAULT',
  'FLOOR_TOUCH',
  'LAST_TOUCH',
  'REACH_OVER',
] as const satisfies readonly ChallengeFault[];

/** Los cinco desenlaces del VCS (copia de `ChallengeOutcomeSchema`). */
export const MESA_CHALLENGE_OUTCOMES = [
  'SUCCESSFUL',
  'UNSUCCESSFUL',
  'INCONCLUSIVE',
  'DECLINED_UNNECESSARY',
  'FOOTAGE_UNAVAILABLE',
] as const satisfies readonly ChallengeOutcome[];

/**
 * Las cuatro primitivas de reconciliación que la pantalla ofrece (BVB 5.10 /
 * §6.2 t7). `NONE` existe en el contrato pero no es una opción de mesa: es el
 * "sin efecto" de un evento que no toca el marcador.
 */
export const MESA_SCORE_ACTIONS = [
  'CONFIRM',
  'INVERT',
  'DELETE_LAST_POINT',
  'REPLAY',
] as const satisfies readonly ScoreAction[];

// ---------------------------------------------------------------------------
// Reglas de conteo (FIVB por defecto; ver la limitación en la cabecera)
// ---------------------------------------------------------------------------

/**
 * Qué desenlace retiene el challenge (copia de `retentionFor` en
 * `packages/core/src/engine/validate.ts`, con los defaults FIVB): sólo
 * `UNSUCCESSFUL` lo pierde. Correcto no lo gasta (art. 2), inconcluyente se
 * retiene (art. 2), el que se cae solo como innecesario también (art. 14), y
 * si el video no estaba disponible la culpa es nuestra, no del equipo.
 */
export function retainsChallenge(outcome: ChallengeOutcome): boolean {
  return outcome !== 'UNSUCCESSFUL';
}

/**
 * Challenges que le quedan a cada equipo DESPUÉS de resolver el abierto. Es el
 * campo `challengesRemaining` del payload, que el motor valida contra su
 * propia cuenta (`CHALLENGE_REMAINING_MISMATCH`): el contador FIVB es de
 * FALLIDOS (arts. 11/20), así que sólo descuenta el equipo que desafió y no
 * retiene. Una revisión de oficio (`team: null`) no le descuenta a nadie.
 */
export function remainingAfterResolution(
  challenge: Pick<ChallengeState, 'open' | 'remaining'>,
  retained: boolean,
): Record<TeamSide, number> {
  const out: Record<TeamSide, number> = { ...challenge.remaining };
  const team = challenge.open?.team ?? null;
  if (team !== null && !retained) out[team] = Math.max(0, out[team] - 1);
  return out;
}

/**
 * Art. 24: un pedido que nunca declaró la falta sólo puede resolverse
 * `UNSUCCESSFUL`. La pantalla apaga los otros cuatro botones con esto, y el
 * motor lo vuelve a exigir (`CHALLENGE_MENU_TIMEOUT`).
 */
export function menuRequiresUnsuccessful(challenge: Pick<ChallengeState, 'open'>): boolean {
  return challenge.open !== null && challenge.open.claimedFault === null;
}

// ---------------------------------------------------------------------------
// Envelope: la mesa numera y firma sus propios eventos
// ---------------------------------------------------------------------------

export interface MesaDraft {
  type: ScoutEvent['type'];
  payload: unknown;
  videoNs?: number;
}

export interface MesaEventMeta {
  matchId: string;
  seq: number;
  actor: Actor;
  /** Reloj de pared; por defecto, ahora. Inyectable para los tests. */
  ts?: string;
}

/**
 * `actor` de los eventos que emite ESTA pantalla: la cuenta de la sesión, con
 * el rol de partido `scorer` (el e-anotador es quien opera la mesa) y `via:
 * 'session'` — que es exactamente el caso que el borde sella `verified`
 * (`auth/actor.ts`, caso 2). Declarar otro `userId` haría rebotar el lote
 * entero con 403 (ADR-016), y está bien que así sea.
 */
export function mesaActor(userId: string, role: MatchRole = 'scorer'): Actor {
  return { userId, role, deviceId: 'mesa-portal', via: 'session' };
}

/** UUID del cliente (offline-safe), con el mismo fallback que la captura. */
export function randomEventId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return `ev_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Borrador → evento canónico completo. Copia la forma de `finalizeDraft`
 * (`@scout/core`) sin ejecutarla en el browser; el test de paridad pasa lo que
 * sale de acá por `ScoutEventSchema.parse` para que la copia no se despegue.
 * `source.input: 'ui'` porque esto ES la mesa, no una tablet de banco.
 */
export function finalizeMesaEvent(draft: MesaDraft, meta: MesaEventMeta): ScoutEvent {
  return {
    id: randomEventId(),
    matchId: meta.matchId,
    seq: meta.seq,
    ts: meta.ts ?? new Date().toISOString(),
    schemaVersion: MESA_SCHEMA_VERSION,
    source: { input: 'ui' },
    actor: meta.actor,
    ...(draft.videoNs !== undefined ? { videoNs: draft.videoNs } : {}),
    type: draft.type,
    payload: draft.payload,
  } as ScoutEvent;
}

/** Mayor `seq` del log crudo (copia de `lastSeqOf` de `@scout/core`; -1 = vacío). */
export function lastSeqOf(events: readonly ScoutEvent[]): number {
  let last = -1;
  for (const ev of events) if (ev.seq > last) last = ev.seq;
  return last;
}

/** El equipo arbitral del partido, como evento (`officials_set`). */
export function officialsSetEvent(officials: readonly OfficialRef[], meta: MesaEventMeta): ScoutEvent {
  return finalizeMesaEvent(
    {
      type: 'officials_set',
      payload: {
        officials: officials.map((o) => ({
          role: o.role,
          name: o.name,
          ...(o.country !== undefined && o.country !== '' ? { country: o.country } : {}),
          ...(o.licenseId !== undefined && o.licenseId !== '' ? { licenseId: o.licenseId } : {}),
          ...(o.userId !== undefined && o.userId !== '' ? { userId: o.userId } : {}),
        })),
      },
    },
    meta,
  );
}

/**
 * Segundo paso del art. 8: el motivo elegido se registra como `correction`
 * sobre el `challenge_requested` (patch superficial del payload, igual que
 * cualquier corrección del log). El motor lo materializa al resolver el log y
 * el pedido pasa de `AWAITING_REASON` a `UNDER_REVIEW`.
 */
export function faultCorrectionEvent(
  requestEventId: string,
  fault: ChallengeFault,
  meta: MesaEventMeta,
): ScoutEvent {
  return finalizeMesaEvent(
    {
      type: 'correction',
      payload: { targetEventId: requestEventId, patch: { claimedFault: fault } },
    },
    meta,
  );
}

// ---------------------------------------------------------------------------
// Resolución + reconciliación: UN lote, no dos pasos
// ---------------------------------------------------------------------------

export interface ReconciliationPlan {
  action: ScoreAction;
  /** Sólo `INVERT`: a quién pasa el punto. */
  invertedTo: TeamSide | null;
  /** El punto vigente que la acción toca (`null` con `CONFIRM`). */
  targetPointId: string | null;
}

export type ResolutionError =
  /** No hay challenge abierto: no hay nada que resolver. */
  | 'not_open'
  /** Art. 24: sin falta declarada, sólo `UNSUCCESSFUL`. */
  | 'menu_requires_unsuccessful'
  /** El log no tiene un punto vigente que tocar (INVERT/DELETE/REPLAY). */
  | 'no_point';

export interface ResolutionInput {
  matchId: string;
  /** Log CRUDO del partido (`GET /events`); acá adentro se resuelve. */
  log: readonly ScoutEvent[];
  challenge: Pick<ChallengeState, 'open' | 'remaining'>;
  outcome: ChallengeOutcome;
  scoreAction: ScoreAction;
  /** Falta observada (art. 13: puede no ser la desafiada). Texto libre. */
  observedFault: string | null;
  /** Dictamen del Challenge Referee (25.2.3), si se dictó. */
  challengeRefereeReport?: string;
  /** Cronómetro de la revisión; se persiste, jamás corta (§6.1). */
  reviewDurationMs: number;
  actor: Actor;
  ts?: string;
}

export interface ResolutionBatch {
  /** El lote a appendear EN ORDEN: `challenge_resolved` + reconciliación. */
  events: ScoutEvent[];
  plan: ReconciliationPlan;
  retained: boolean;
  remaining: Record<TeamSide, number>;
}

/**
 * Compone el lote atómico que cierra la interrupción (§6.2, t5-t7).
 *
 * La reconciliación copia `reconcileScore` de `@scout/core/engine/challenge`
 * (la paridad la cuida el test): **no es un undo genérico** — `INVERT` cambia
 * el equipo al saque y `DELETE_LAST_POINT`/`REPLAY` se lo devuelven al mismo
 * sacador. Todo va en UN `POST /events` porque el lote del API es atómico: o
 * entra la resolución CON su efecto en el marcador, o no entra nada.
 */
export function buildResolutionBatch(input: ResolutionInput): ResolutionBatch | { error: ResolutionError } {
  const open = input.challenge.open;
  if (open === null) return { error: 'not_open' };
  if (menuRequiresUnsuccessful(input.challenge) && input.outcome !== 'UNSUCCESSFUL') {
    return { error: 'menu_requires_unsuccessful' };
  }

  const retained = retainsChallenge(input.outcome);
  const remaining = remainingAfterResolution(input.challenge, retained);
  let seq = lastSeqOf(input.log);
  const meta = (): MesaEventMeta => {
    seq += 1;
    return {
      matchId: input.matchId,
      seq,
      actor: input.actor,
      ...(input.ts !== undefined ? { ts: input.ts } : {}),
    };
  };

  const resolved = finalizeMesaEvent(
    {
      type: 'challenge_resolved',
      payload: {
        requestEventId: open.requestEventId,
        outcome: input.outcome,
        observedFault: input.observedFault,
        ...(input.challengeRefereeReport !== undefined && input.challengeRefereeReport !== ''
          ? { challengeRefereeReport: input.challengeRefereeReport }
          : {}),
        challengeRetained: retained,
        scoreAction: input.scoreAction,
        challengesRemaining: remaining,
        reviewDurationMs: Math.max(0, Math.round(input.reviewDurationMs)),
        cameras: [],
      },
    },
    meta(),
  );

  if (input.scoreAction === 'CONFIRM' || input.scoreAction === 'NONE') {
    return {
      events: [resolved],
      plan: { action: input.scoreAction, invertedTo: null, targetPointId: null },
      retained,
      remaining,
    };
  }

  // Último punto VIGENTE del log efectivo (undos y correcciones ya aplicados).
  const effective = normalizeLog(input.log);
  const lastPoint = [...effective].reverse().find((n) => n.event.type === 'point')?.event;
  if (lastPoint === undefined || lastPoint.type !== 'point') return { error: 'no_point' };

  const undo = finalizeMesaEvent(
    { type: 'undo', payload: { targetEventId: lastPoint.id } },
    meta(),
  );

  if (input.scoreAction === 'DELETE_LAST_POINT' || input.scoreAction === 'REPLAY') {
    return {
      events: [resolved, undo],
      plan: { action: input.scoreAction, invertedTo: null, targetPointId: lastPoint.id },
      retained,
      remaining,
    };
  }

  // INVERT: el punto era del otro. Se revoca y nace uno nuevo con motivo
  // `challenge_adjustment` — el único punto del acta que no lo produjo una
  // jugada, y por eso se circula en la columna POINTS.
  const p = lastPoint.payload;
  const beforeHome = p.homeScore - (p.wonBy === 'home' ? 1 : 0);
  const beforeAway = p.awayScore - (p.wonBy === 'away' ? 1 : 0);
  const winner: TeamSide = p.wonBy === 'home' ? 'away' : 'home';
  const point = finalizeMesaEvent(
    {
      type: 'point',
      payload: {
        wonBy: winner,
        reason: 'challenge_adjustment',
        homeScore: beforeHome + (winner === 'home' ? 1 : 0),
        awayScore: beforeAway + (winner === 'away' ? 1 : 0),
      },
      ...(lastPoint.videoNs !== undefined ? { videoNs: lastPoint.videoNs } : {}),
    },
    meta(),
  );

  return {
    events: [resolved, undo, point],
    plan: { action: 'INVERT', invertedTo: winner, targetPointId: lastPoint.id },
    retained,
    remaining,
  };
}

// ---------------------------------------------------------------------------
// El tablero de firmas: quién firmó, quién falta, quién sigue
// ---------------------------------------------------------------------------

export interface SignerSlot {
  role: MatchRole;
  /** Sólo capitanes/delegados: de qué banco. */
  team: TeamSide | null;
  /** Titular acreditado (designación) o declarado (oficiales); `null` = nadie. */
  holderName: string | null;
  /** Designación ACTIVA con la que ese titular puede hacer el step-up. */
  credential: MatchCredential | null;
  /** La firma de esta etapa, si ya está. */
  signature: SheetSignatureRef | null;
  /**
   * Regla 27.2.3.3, el único corte duro del motor: hasta que firme el
   * anotador —cuya firma congela el digest— nadie más puede firmar el cierre.
   */
  blockedByScorer: boolean;
}

function activeOf(
  assignments: readonly MatchCredential[],
  role: MatchRole,
  team: TeamSide | null,
): MatchCredential | null {
  return (
    assignments.find(
      (a) => !a.revoked && a.role === role && (team === null || a.team === team),
    ) ?? null
  );
}

function signatureOf(
  signatures: readonly SheetSignatureRef[],
  stage: 'PREMATCH' | 'POSTMATCH',
  role: MatchRole,
  team: TeamSide | null,
): SheetSignatureRef | null {
  return (
    signatures.find(
      (s) => s.stage === stage && s.role === role && (team === null || s.team === team),
    ) ?? null
  );
}

/**
 * Las firmas del CIERRE, en el orden de la instrucción 4.10. Un lugar por rol
 * —dos para los capitanes, uno por banco— exista o no la designación: la mesa
 * tiene que VER el hueco ("capitán visitante: sin acreditar"), no descubrirlo
 * cuando el árbitro ya se quiere ir.
 */
export function closingBoard(
  document: Pick<SheetState, 'officials' | 'signatures'>,
  assignments: readonly MatchCredential[],
): SignerSlot[] {
  const scorerSigned =
    signatureOf(document.signatures, 'POSTMATCH', 'scorer', null) !== null;

  const slots: SignerSlot[] = [];
  for (const role of MESA_SIGNING_ORDER) {
    const teams: Array<TeamSide | null> = role === 'team_captain' ? ['home', 'away'] : [null];
    for (const team of teams) {
      const credential = activeOf(assignments, role, team);
      const official = document.officials.find((o) => o.role === role) ?? null;
      slots.push({
        role,
        team,
        holderName: credential?.holderName ?? official?.name ?? null,
        credential,
        signature: signatureOf(document.signatures, 'POSTMATCH', role, team),
        blockedByScorer: role !== 'scorer' && !scorerSigned,
      });
    }
  }
  return slots;
}

/**
 * Las firmas PREMATCH que congelan el roster (regla 4.1.3), por equipo. Es la
 * "verificación de roster" de la pantalla: mientras estén sin firmar, el
 * plantel de ese banco todavía puede cambiar.
 */
export function rosterBoard(
  document: Pick<SheetState, 'officials' | 'signatures'>,
  assignments: readonly MatchCredential[],
): Record<TeamSide, SignerSlot[]> {
  const board: Record<TeamSide, SignerSlot[]> = { home: [], away: [] };
  for (const team of ['home', 'away'] as const) {
    for (const role of MESA_PREMATCH_ROLES) {
      const credential = activeOf(assignments, role, team);
      board[team].push({
        role,
        team,
        holderName: credential?.holderName ?? null,
        credential,
        signature: signatureOf(document.signatures, 'PREMATCH', role, team),
        blockedByScorer: false,
      });
    }
  }
  return board;
}

/** El próximo que tiene que firmar el cierre, según el orden 4.10. */
export function nextSigner(slots: readonly SignerSlot[]): SignerSlot | null {
  return slots.find((s) => s.signature === null && s.holderName !== null) ?? null;
}

// ---------------------------------------------------------------------------
// Caché de tokens emitidos: para que el titular firme EN la mesa
// ---------------------------------------------------------------------------

/**
 * La firma viaja con la sesión del FIRMANTE (`resolveSigner` mira la
 * credencial del request), así que para que el árbitro firme en la laptop de
 * la mesa hace falta canjear SU credencial. El token sólo existe en claro en
 * el momento de emitirla — se guarda acá, en el navegador de la mesa, que es
 * el mismo lugar donde ese token se mostró en pantalla. Se borra al cerrar el
 * acta. Un F5 no obliga a re-emitir; limpiar el navegador, sí.
 */
export const MESA_TOKENS_PREFIX = 'scout.portal.mesa.tokens.';

export function mesaTokensKey(matchId: string): string {
  return `${MESA_TOKENS_PREFIX}${matchId}`;
}

export type IssuedTokens = Record<string, string>;

export interface KeyValueStoreLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function loadIssuedTokens(matchId: string, store: KeyValueStoreLike): IssuedTokens {
  try {
    const raw = store.getItem(mesaTokensKey(matchId));
    if (raw === null || raw === '') return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: IssuedTokens = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export function saveIssuedToken(
  matchId: string,
  credentialId: string,
  token: string,
  store: KeyValueStoreLike,
): void {
  try {
    const tokens = loadIssuedTokens(matchId, store);
    tokens[credentialId] = token;
    store.setItem(mesaTokensKey(matchId), JSON.stringify(tokens));
  } catch {
    // Modo privado o cuota llena: el token queda sólo en la pantalla. La firma
    // desde la mesa va a pedir re-emitir; no es motivo para tirar la emisión.
  }
}

export function clearIssuedTokens(matchId: string, store: KeyValueStoreLike): void {
  try {
    store.removeItem(mesaTokensKey(matchId));
  } catch {
    /* nada que limpiar si no se pudo ni leer */
  }
}
